import { CSAP_ENROLLMENT_OPT_IN, CSAP_ENROLLMENT_OPT_OUT } from 'utils/constants';
import { useFormatPrice } from 'utils/hooks/useFormatPrice/useFormatPrice';
import {
  BillingContainer,
  CsapEnrollmentOption,
  CsapExpiration,
  CsapTable,
  CsapTableHead,
  CsapTableRow,
  CsapTermsAndConditions,
  CsapEnrollmentValidation,
} from './styledComponents';
import { getI18NText } from 'I18N/I18N';
import { Fragment } from 'react';
import { Flex } from '@Calix-Commerce/design-system';
import { CsapGroupRecord } from 'utils/helpers/checkout';

interface CsapProgramProps {
  csapEnrolled: string;
  csapPricePerGroup: CsapGroupRecord;
  csapEnrolledValidation?: string;
  setErrors: (errors: any) => void;
  setCsapEnrolled: (enrolled: string) => void;
}

export const CsapProgram = ({
  csapPricePerGroup,
  csapEnrolled,
  setCsapEnrolled,
  setErrors,
  csapEnrolledValidation,
}: CsapProgramProps) => {
  const formatPrice = useFormatPrice();

  return (
    <BillingContainer>
      <CsapExpiration>
        <Flex direction="column">
          <p>
            <strong>
              {getI18NText('CSAP_SECTION_EXPIRATION')} (
              <a
                href="https://www.calix.com/my-calix/product-lead-times.html"
                rel="noopener noreferrer"
                target="_blank"
              >
                {getI18NText('CSAP_SECTION_DISCLAIMER_CTA_RATES')}
              </a>
              )
            </strong>
          </p>
          <CsapTermsAndConditions>
            <CsapEnrollmentOption
              attributeToBeDisplayed={'text'}
              className={csapEnrolledValidation ? 'with-error' : ''}
              showFilter={false}
              defaultText={getI18NText('CSAP_SECTION_SELECT_OPTION_LABEL')}
              options={[
                { id: CSAP_ENROLLMENT_OPT_IN, text: getI18NText('CSAP_SECTION_OPT_IN') },
                { id: CSAP_ENROLLMENT_OPT_OUT, text: getI18NText('CSAP_SECTION_OPT_OUT') },
              ]}
              selectedOptionId={csapEnrolled || ''}
              onSelectOption={(newSelectedOption) => {
                setCsapEnrolled(newSelectedOption.id);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  csapEnrolled: '',
                }));
              }}
            />
            <Flex direction="column">
              <label htmlFor="csap-enrolled">
                {getI18NText('CSAP_SECTION_DISCLAIMER_1')}
                <a
                  href="https://www.calix.com/content/dam/calix/mycalix-misc/micro/CSAP.pdf"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {getI18NText('CSAP_SECTION_DISCLAIMER_CTA_OPT_IN')}
                </a>
                {getI18NText('CSAP_SECTION_DISCLAIMER_2')}
                <strong>{getI18NText('CSAP_SECTION_DISCLAIMER_3')}</strong>
              </label>
              {csapEnrolledValidation && (
                <CsapEnrollmentValidation>{csapEnrolledValidation}</CsapEnrollmentValidation>
              )}
            </Flex>
          </CsapTermsAndConditions>
        </Flex>
      </CsapExpiration>
      <CsapTable>
        <CsapTableHead>
          <tr>
            <th>{getI18NText('CSAP_SECTION_HEADER_PART_NUMBER')}</th>
            <th>{getI18NText('CSAP_SECTION_HEADER_PART_DESCRIPTION')}</th>
            <th>{getI18NText('CSAP_SECTION_HEADER_AMOUNT')}</th>
          </tr>
        </CsapTableHead>
        <tbody>
          {Object.entries(csapPricePerGroup).map(([groupName, groupDetails]) => (
            <Fragment key={groupName}>
              {Object.entries(groupDetails.parts).map(([partNumber, partDetails]) => (
                <CsapTableRow key={partNumber}>
                  <td>{partNumber}</td>
                  <td>{partDetails.partName}</td>
                  <td>{formatPrice(groupDetails.pricePerUnit * partDetails.partQuantity)}</td>
                </CsapTableRow>
              ))}
              <CsapTableRow className={'surcharge-row'} key={`${groupName}-total`}>
                <td colSpan={2}>{groupName}</td>
                <td>{formatPrice(groupDetails.pricePerUnit * groupDetails.totalQuantity)}</td>
              </CsapTableRow>
            </Fragment>
          ))}
        </tbody>{' '}
      </CsapTable>
      <p>{getI18NText('CSAP_SECTION_NOTE')}</p>
    </BillingContainer>
  );
};
